import { type FC, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

// common
import MyLink from 'common/components/MyLink';
import NewPagination from 'common/components/NewPagination';
import NotFoundBox from 'common/components/NotFoundBox';
import useIsParent from 'common/hook/useIsParent';
import { ROUTES_PATH } from 'layout/constants';

// screen
import { formatUTC } from 'modules/map/components/pdf-print-form/utils';
import type { FoodTraceabilityRow } from 'modules/map/components/food-traceability/types';

interface Props {
  rows: FoodTraceabilityRow[];
  customerOperationCode: string;
  initialPage?: number;
  initialRowsPerPage?: number;
}

const formatNumber = (value: number | undefined, fractionDigits = 0) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  return (
    <FormattedNumber
      value={value}
      minimumFractionDigits={fractionDigits}
      maximumFractionDigits={fractionDigits}
    />
  );
};

const FoodTraceabilityTable: FC<Props> = ({
  rows,
  customerOperationCode,
  initialPage,
  initialRowsPerPage,
}) => {
  const [page, setPage] = useState(initialPage ?? 1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage ?? 20);
  const isParent = useIsParent();

  const total = rows.length;
  const start = (page - 1) * rowsPerPage;
  const pageRows = rows.slice(start, start + rowsPerPage);

  const buildSupplierHref = (supplierOperationCode?: string) => {
    if (!supplierOperationCode) return undefined;
    return `/portal/${ROUTES_PATH.supplier.index}/${ROUTES_PATH.supplier.detail(
      'thuc-pham-che-bien',
      customerOperationCode,
      supplierOperationCode
    )}`;
  };

  if (!total) {
    return <NotFoundBox />;
  }

  return (
    <Box className="flex flex-col flex-1 overflow-hidden">
      <TableContainer
        sx={{
          flex: 1,
          '& td, & th': {
            textAlign: 'center',
            padding: 1,
          },
        }}
      >
        <Table size="small">
          <TableHead
            sx={(theme) => ({
              position: 'sticky',
              top: 0,
              bgcolor: theme.palette.background.paper,
              zIndex: 1,
            })}
          >
            <TableRow>
              <TableCell style={{ width: 60 }}>
                <FormattedMessage id="stt" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="map.foodTraceability.actualEatingDate" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="map.foodTraceability.dishName" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="map.foodTraceability.ingredientName" />
              </TableCell>
              <TableCell sx={{ textAlign: 'right !important' }}>
                <FormattedMessage id="map.foodTraceability.inputQuantity" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="map.foodTraceability.unit" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="map.foodTraceability.supplier" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row) => {
              return (
                <TableRow key={row.id}>
                  <TableCell>{row.stt}</TableCell>
                  <TableCell>{row.menu_date ? formatUTC(row.menu_date, 'L') : ''}</TableCell>
                  <TableCell>{row.dish_display_name || ''}</TableCell>
                  <TableCell>{row.selected_warehouse_food_name || ''}</TableCell>
                  <TableCell sx={{ textAlign: 'right !important' }}>
                    {formatNumber(row.standard_quantity, 3)}
                  </TableCell>
                  <TableCell>{row.unit || ''}</TableCell>
                  <TableCell>
                    {row.supplier_name ? (
                      (() => {
                        // Phụ huynh: bỏ link NCC (docx §4) -> href undefined => render text thường.
                        const href = isParent
                          ? undefined
                          : buildSupplierHref(row.supplier_operation_code);
                        return href ? (
                          <MyLink
                            to={{ pathname: href }}
                            state={{
                              from: 'foodTraceability',
                              focusOrg: customerOperationCode,
                              tablePage: page,
                              tableRowsPerPage: rowsPerPage,
                            }}
                            sx={{
                              color: 'error.main',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                          >
                            {row.supplier_name}
                          </MyLink>
                        ) : (
                          row.supplier_name
                        );
                      })()
                    ) : (
                      ''
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <NewPagination
        page={page}
        rowsPerPage={rowsPerPage}
        count={total}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(Number(value));
          setPage(1);
        }}
      />
    </Box>
  );
};

export default FoodTraceabilityTable;
